import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Divider,
  Alert,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CloseIcon from "@mui/icons-material/Close";
import PhoneWithCountryField from "../shared/PhoneWithCountryField";
import {
  formatPhoneDisplay,
  isValidFullPhone,
  normalizeFullPhone,
} from "../../utils/phoneCountryCodes";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { obtenerFechaHoyISO } from "../../utils/horaLocal";

export default function POSVenta({ store }) {
  const [carrito, setCarrito] = useState([]);
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [montoEfectivo, setMontoEfectivo] = useState("");
  const [montoQR, setMontoQR] = useState("");
  const [codigoInput, setCodigoInput] = useState("");
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [nuevoClienteNombre, setNuevoClienteNombre] = useState("");
  const [nuevoClienteWhatsapp, setNuevoClienteWhatsapp] = useState("");
  const [codigoCampanaVenta, setCodigoCampanaVenta] = useState("");
  const [descuentoManualStr, setDescuentoManualStr] = useState("");
  const [tipoDescuentoManual, setTipoDescuentoManual] = useState("MONTO"); // "MONTO" | "PORCENTAJE"
  const [feedback, setFeedback] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [procesandoVenta, setProcesandoVenta] = useState(false);
  const [fechaRegistro, setFechaRegistro] = useState(() => obtenerFechaHoyISO());
  const inputRef = useRef(null);

  const moneda = store.config.moneda;

  const clientes = store.clientesSegmentados || [];
  const sugerenciasProducto = useMemo(() => {
    const termino = codigoInput.trim();
    if (!termino) return [];
    return store.buscarProductos(termino).slice(0, 8);
  }, [codigoInput, store]);
  const campanas = store.campanasSociales || [];
  const campanasConCodigo = useMemo(
    () => campanas.filter((c) => String(c.codigo || "").trim()),
    [campanas]
  );

  // Auto-focus inicial sin secuestrar el foco de otros campos.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Limpiar feedback después de 3s
  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  const total = useMemo(
    () =>
      carrito.reduce(
        (acc, item) => acc + item.cantidad * item.precio_unitario,
        0
      ),
    [carrito]
  );

  const interesesClienteVinculados = useMemo(() => {
    if (!clienteSeleccionado?.id) return [];
    return (store.interesesCatalogo || []).filter(
      (i) => i.cliente_id === clienteSeleccionado.id
    );
  }, [clienteSeleccionado?.id, store.interesesCatalogo]);

  const promocionesPreview = store.evaluarPromocionesCarrito(carrito, {
    canal: "TIENDA",
    clienteId: clienteSeleccionado?.id,
    codigoCampana: codigoCampanaVenta,
  });

  // Manual discount: computed on the subtotal BEFORE promotions to avoid stacking issues.
  const descuentoManualMonto = useMemo(() => {
    const raw = parseFloat(descuentoManualStr) || 0;
    if (raw <= 0) return 0;
    if (tipoDescuentoManual === "PORCENTAJE") {
      return Math.min(total, parseFloat(((total * raw) / 100).toFixed(2)));
    }
    return Math.min(total, parseFloat(raw.toFixed(2)));
  }, [descuentoManualStr, tipoDescuentoManual, total]);

  const totalFinal = Math.max(
    0,
    parseFloat(((promocionesPreview?.totalFinal ?? total) - descuentoManualMonto).toFixed(2))
  );
  const totalFinalRedondeado = parseFloat(totalFinal.toFixed(2));

  const descuentosFinales = useMemo(
    () => [
      ...(promocionesPreview.promocionesAplicadas || []),
      ...(descuentoManualMonto > 0
        ? [
            {
              promo_id: "MANUAL",
              nombre:
                tipoDescuentoManual === "PORCENTAJE"
                  ? `Descuento manual ${parseFloat(descuentoManualStr)}%`
                  : "Descuento manual",
              tipo: "DESCUENTO_MANUAL",
              monto: descuentoManualMonto,
            },
          ]
        : []),
    ],
    [
      promocionesPreview.promocionesAplicadas,
      descuentoManualMonto,
      tipoDescuentoManual,
      descuentoManualStr,
    ]
  );

  const totalDescuentos = useMemo(
    () => descuentosFinales.reduce((acc, d) => acc + (Number(d.monto) || 0), 0),
    [descuentosFinales]
  );

  const metodoPagoLabel = {
    EFECTIVO: "Efectivo",
    TARJETA: "Tarjeta",
    QR: "QR / Transferencia",
    MIXTO: "Mixto (efectivo + QR)",
  }[metodoPago] || metodoPago;

  useEffect(() => {
    if (metodoPago === "MIXTO") {
      setMontoEfectivo((prev) => {
        if (prev !== "") return prev;
        return totalFinalRedondeado.toFixed(2);
      });
      setMontoQR((prev) => (prev !== "" ? prev : "0.00"));
    }
  }, [metodoPago, totalFinalRedondeado]);

  const agregarProductoAlCarrito = useCallback((producto) => {
    if (!producto) return;

    if (producto.stock <= 0) {
      setFeedback({
        tipo: "warning",
        msg: `"${producto.nombre}" sin stock disponible`,
      });
      return;
    }

    setCarrito((prev) => {
      const existente = prev.find((i) => i.producto_id === producto.id);
      if (existente) {
        if (existente.cantidad >= producto.stock) {
          setFeedback({
            tipo: "warning",
            msg: `Stock máximo alcanzado para "${producto.nombre}"`,
          });
          return prev;
        }
        return prev.map((i) =>
          i.producto_id === producto.id ? { ...i, cantidad: i.cantidad + 1 } : i
        );
      }
      return [
        ...prev,
        {
          producto_id: producto.id,
          nombre: producto.nombre,
          marca: producto.marca,
          codigo_barras: producto.codigo_barras,
          sku: producto.sku,
          categoria: producto.categoria,
          talla: producto.talla,
          color: producto.color,
          precio_unitario: producto.precio_venta,
          costo_unitario: store.obtenerCostoPromedioProducto
            ? store.obtenerCostoPromedioProducto(producto.id)
            : producto.precio_compra,
          cantidad: 1,
          stock_disponible: producto.stock,
        },
      ];
    });

    setFeedback({
      tipo: "success",
      msg: `✓ ${producto.nombre} agregado`,
    });
    setCodigoInput("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [store]);

  const handleScan = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();

      const termino = codigoInput.trim();
      if (!termino) return;

      const porCodigo = store.buscarProductoPorCodigo(termino);
      if (porCodigo) {
        agregarProductoAlCarrito(porCodigo);
        return;
      }

      const coincidencias = store.buscarProductos(termino);
      if (coincidencias.length === 0) {
        setFeedback({
          tipo: "error",
          msg: `Producto no encontrado: ${termino}`,
        });
        return;
      }

      const lowerTermino = termino.toLowerCase();
      const exacta = coincidencias.find(
        (p) =>
          String(p.nombre || "").toLowerCase() === lowerTermino ||
          String(p.codigo_barras || "").toLowerCase() === lowerTermino ||
          String(p.sku || "").toLowerCase() === lowerTermino
      );

      if (exacta) {
        agregarProductoAlCarrito(exacta);
        return;
      }

      if (coincidencias.length === 1) {
        agregarProductoAlCarrito(coincidencias[0]);
        return;
      }

      setFeedback({
        tipo: "info",
        msg: "Hay varias coincidencias, selecciona una sugerencia.",
      });
    },
    [agregarProductoAlCarrito, codigoInput, store]
  );

  const seleccionarProductoSugerido = useCallback(
    (_, value) => {
      if (!value || typeof value === "string") return;
      agregarProductoAlCarrito(value);
    },
    [agregarProductoAlCarrito]
  );

  const cambiarInputProducto = useCallback((_, value, reason) => {
    if (reason === "reset") return;
    setCodigoInput(value);
  }, []);

  const cambiarCantidad = (productoId, delta) => {
    setCarrito((prev) =>
      prev
        .map((item) => {
          if (item.producto_id !== productoId) return item;
          const nuevaCantidad = item.cantidad + delta;
          if (nuevaCantidad <= 0) return null;
          if (nuevaCantidad > item.stock_disponible) {
            setFeedback({ tipo: "warning", msg: "Stock insuficiente" });
            return item;
          }
          return { ...item, cantidad: nuevaCantidad };
        })
        .filter(Boolean)
    );
  };

  const eliminarDelCarrito = (productoId) => {
    setCarrito((prev) => prev.filter((i) => i.producto_id !== productoId));
  };

  const validarPagoMixto = () => {
    const efectivo = Math.max(0, parseFloat(montoEfectivo) || 0);
    const qr = Math.max(0, parseFloat(montoQR) || 0);
    const pagoMixto = parseFloat((efectivo + qr).toFixed(2));

    if (metodoPago !== "MIXTO") return { ok: true, efectivo: 0, qr: 0 };

    if (efectivo <= 0 && qr <= 0) {
      return {
        ok: false,
        msg: "Ingresa montos de efectivo y/o QR para pago mixto",
      };
    }

    if (Math.abs(pagoMixto - totalFinalRedondeado) > 0.01) {
      return {
        ok: false,
        msg: `Pago mixto inválido: Efectivo + QR debe ser ${totalFinalRedondeado.toFixed(2)}`,
      };
    }

    return { ok: true, efectivo, qr };
  };

  const resetFormVenta = () => {
    setCarrito([]);
    setMetodoPago("EFECTIVO");
    setMontoEfectivo("");
    setMontoQR("");
    setCodigoCampanaVenta("");
    setDescuentoManualStr("");
    setTipoDescuentoManual("MONTO");
    setClienteSeleccionado(null);
    setNuevoClienteNombre("");
    setNuevoClienteWhatsapp("");
    setFechaRegistro(obtenerFechaHoyISO());
  };

  const resolverClienteParaVenta = async () => {
    if (clienteSeleccionado?.id) {
      return { clienteId: clienteSeleccionado.id, nombre: clienteSeleccionado.nombre };
    }

    const nombre = nuevoClienteNombre.trim();
    const whatsapp = normalizeFullPhone(nuevoClienteWhatsapp);
    if (!nombre && !whatsapp) {
      return { clienteId: null, nombre: null };
    }

    if (!nombre || !whatsapp) {
      return { clienteId: null, nombre: null };
    }

    if (!isValidFullPhone(whatsapp)) {
      return {
        clienteId: null,
        error: "WhatsApp inválido: usa código de país (+591) y el número completo",
      };
    }

    const clienteId = await store.upsertCliente({
      nombre,
      whatsapp,
      consentimiento_whatsapp: true,
      estilo_preferido: "CASUAL",
    });

    if (!clienteId) {
      return { clienteId: null, error: "No se pudo guardar el cliente" };
    }

    const cliente = {
      id: clienteId,
      nombre,
      whatsapp,
      stats: { segmento: "NUEVO" },
    };
    setClienteSeleccionado(cliente);
    return { clienteId, nombre };
  };

  const ejecutarVenta = async (clienteId, nombreCliente) => {
    const pago = validarPagoMixto();
    if (!pago.ok) {
      setFeedback({ tipo: "warning", msg: pago.msg });
      return false;
    }

    const result = await store.registrarVenta(carrito, metodoPago, {
      clienteId: clienteId || null,
      canal: "TIENDA",
      descuentos: descuentosFinales,
      codigoCampana: codigoCampanaVenta,
      fecha: fechaRegistro,
      metodoPagoDetalle:
        metodoPago === "MIXTO"
          ? { efectivo: pago.efectivo, qr: pago.qr }
          : null,
    });

    if (result?.ok) {
      setFeedback({
        tipo: "success",
        msg: nombreCliente
          ? `Venta registrada para ${nombreCliente}`
          : "Venta registrada exitosamente",
      });
      resetFormVenta();
      return true;
    }

    if (result?.queued) {
      setFeedback({
        tipo: "warning",
        msg: "Sin conexión — venta guardada en cola. Se enviará al reconectar.",
      });
      resetFormVenta();
      return true;
    }

    setFeedback({ tipo: "error", msg: "Error al registrar la venta" });
    return false;
  };

  const abrirConfirmacion = () => {
    if (carrito.length === 0) return;
    const pago = validarPagoMixto();
    if (!pago.ok) {
      setFeedback({ tipo: "warning", msg: pago.msg });
      return;
    }
    setConfirmOpen(true);
  };

  const confirmarCobro = async () => {
    const cliente = await resolverClienteParaVenta();
    if (cliente.error) {
      setFeedback({ tipo: "warning", msg: cliente.error });
      return;
    }

    setProcesandoVenta(true);
    try {
      const ok = await ejecutarVenta(cliente.clienteId, cliente.nombre);
      if (ok) setConfirmOpen(false);
    } finally {
      setProcesandoVenta(false);
    }
  };

  const limpiarCarrito = () => {
    if (carrito.length > 0 && window.confirm("¿Limpiar el carrito?")) {
      setCarrito([]);
    }
  };

  const guardarClienteRapido = async () => {
    if (clienteSeleccionado) return;

    if (!nuevoClienteNombre.trim() || !nuevoClienteWhatsapp.trim()) {
      setFeedback({
        tipo: "warning",
        msg: "Completa nombre y WhatsApp para registrar cliente",
      });
      return;
    }

    const whatsapp = normalizeFullPhone(nuevoClienteWhatsapp);
    if (!isValidFullPhone(whatsapp)) {
      setFeedback({
        tipo: "warning",
        msg: "WhatsApp inválido: selecciona país (+591) y el número completo",
      });
      return;
    }

    const clienteId = await store.upsertCliente({
      nombre: nuevoClienteNombre.trim(),
      whatsapp,
      consentimiento_whatsapp: true,
      estilo_preferido: "CASUAL",
    });

    if (!clienteId) {
      setFeedback({ tipo: "error", msg: "No se pudo guardar el cliente" });
      return;
    }

    setClienteSeleccionado({
      id: clienteId,
      nombre: nuevoClienteNombre.trim(),
      whatsapp,
      stats: {
        segmento: "NUEVO",
      },
    });
    setFeedback({ tipo: "success", msg: "Cliente guardado y seleccionado" });
    setNuevoClienteNombre("");
    setNuevoClienteWhatsapp("");
  };

  return (
    <Box>
      {/* Barra de escaneo */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          bgcolor: "primary.50",
          borderColor: "primary.200",
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PointOfSaleIcon color="primary" sx={{ fontSize: 32 }} />
            <Autocomplete
              freeSolo
              fullWidth
              options={sugerenciasProducto}
              filterOptions={(x) => x}
              inputValue={codigoInput}
              onInputChange={cambiarInputProducto}
              onChange={seleccionarProductoSugerido}
              noOptionsText={
                codigoInput.trim()
                  ? "Sin coincidencias"
                  : "Escribe nombre, código o SKU"
              }
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return `${option.nombre} · ${option.codigo_barras}${
                  option.sku ? ` · ${option.sku}` : ""
                }`;
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {option.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.marca || "Sin marca"} · {option.codigo_barras}
                      {option.sku ? ` · ${option.sku}` : ""}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  onKeyDown={handleScan}
                  placeholder="Busca por nombre, código o SKU y presiona Enter..."
                  fullWidth
                  size="medium"
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      fontSize: "1.1rem",
                    },
                  }}
                />
              )}
            />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mt={2}>
            <Autocomplete
              options={clientes}
              value={clienteSeleccionado}
              onChange={(_, value) => setClienteSeleccionado(value)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(c) =>
                `${c.nombre} (${formatPhoneDisplay(c.whatsapp) || c.whatsapp || "sin WA"})`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente (opcional)"
                  placeholder="Buscar por nombre o WhatsApp"
                  size="small"
                />
              )}
              sx={{ flex: 1 }}
            />
            {!clienteSeleccionado && (
              <>
                <TextField
                  label="Nombre rapido"
                  size="small"
                  value={nuevoClienteNombre}
                  onChange={(e) => setNuevoClienteNombre(e.target.value)}
                  sx={{ minWidth: 180 }}
                />
                <Box sx={{ minWidth: { xs: "100%", md: 320 }, flex: 1 }}>
                  <PhoneWithCountryField
                    label="WhatsApp rápido"
                    value={nuevoClienteWhatsapp}
                    onChange={setNuevoClienteWhatsapp}
                    helperText="+591 por defecto"
                  />
                </Box>
                <Button variant="outlined" onClick={guardarClienteRapido}>
                  Guardar Cliente
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Feedback */}
      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      {/* Contenido: Carrito + Resumen */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        {/* Carrito */}
        <Box flex={2}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Carrito ({carrito.length} items)
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell>Producto</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell align="center">Cantidad</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="center" width={60}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {carrito.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Escanea un producto para comenzar
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  carrito.map((item) => (
                    <TableRow key={item.producto_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.talla || "UNICA"} / {item.color || "SIN COLOR"} · {item.sku || "SIN SKU"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                          {item.codigo_barras}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          spacing={0}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              cambiarCantidad(item.producto_id, -1)
                            }
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Chip
                            label={item.cantidad}
                            size="small"
                            sx={{ minWidth: 36, fontWeight: 700 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              cambiarCantidad(item.producto_id, 1)
                            }
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {store.config.moneda}{" "}
                        {item.precio_unitario.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {store.config.moneda}{" "}
                          {(item.cantidad * item.precio_unitario).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            eliminarDelCarrito(item.producto_id)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Panel de resumen */}
        <Card
          variant="outlined"
          sx={{ flex: 1, minWidth: 280, alignSelf: "flex-start" }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Resumen
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1} mb={2}>
              {carrito.map((item) => (
                <Stack
                  key={item.producto_id}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" color="text.secondary">
                    {item.nombre} x{item.cantidad}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {(item.cantidad * item.precio_unitario).toFixed(2)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {clienteSeleccionado && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Cliente
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700}>{clienteSeleccionado.nombre}</Typography>
                  <Chip
                    size="small"
                    label={
                      clienteSeleccionado?.stats?.segmento
                        ? `Segmento ${clienteSeleccionado.stats.segmento}`
                        : "Segmento NUEVO"
                    }
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </Box>
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="h6" fontWeight={700}>
                Subtotal
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {store.config.moneda} {total.toFixed(2)}
              </Typography>
            </Stack>

            {promocionesPreview.promocionesAplicadas.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Promociones aplicadas
                </Typography>
                <Stack spacing={0.5}>
                  {promocionesPreview.promocionesAplicadas.map((promo) => (
                    <Stack
                      key={promo.promo_id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Chip size="small" label={promo.nombre} variant="outlined" />
                      <Typography variant="body2" color="success.main" fontWeight={700}>
                        -{store.config.moneda} {promo.monto.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Descuento manual */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} mb={0.75}>
                <DiscountIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Descuento adicional
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <ToggleButtonGroup
                  value={tipoDescuentoManual}
                  exclusive
                  onChange={(_, v) => { if (v) setTipoDescuentoManual(v); }}
                  size="small"
                >
                  <ToggleButton value="MONTO" sx={{ px: 1.5, py: 0.5, fontSize: 12 }}>
                    {store.config.moneda}
                  </ToggleButton>
                  <ToggleButton value="PORCENTAJE" sx={{ px: 1.5, py: 0.5, fontSize: 12 }}>
                    %
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  type="number"
                  placeholder={tipoDescuentoManual === "PORCENTAJE" ? "0" : "0.00"}
                  inputProps={{ min: 0, step: tipoDescuentoManual === "PORCENTAJE" ? "1" : "0.01", max: tipoDescuentoManual === "PORCENTAJE" ? 100 : undefined }}
                  value={descuentoManualStr}
                  onChange={(e) => setDescuentoManualStr(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Stack>
              {descuentoManualMonto > 0 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    Descuento aplicado
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight={700}>
                    -{store.config.moneda} {descuentoManualMonto.toFixed(2)}
                  </Typography>
                </Stack>
              )}
            </Box>

            <Stack direction="row" justifyContent="space-between" mb={3}>
              <Typography variant="h5" fontWeight={800}>
                Total Final
              </Typography>
              <Typography variant="h5" fontWeight={800} color="primary">
                {store.config.moneda} {totalFinal.toFixed(2)}
              </Typography>
            </Stack>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Método de pago</InputLabel>
              <Select
                value={metodoPago}
                onChange={(e) => setMetodoPago(e.target.value)}
                label="Método de pago"
              >
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="TARJETA">Tarjeta</MenuItem>
                <MenuItem value="QR">QR / Transferencia</MenuItem>
                <MenuItem value="MIXTO">Mixto</MenuItem>
              </Select>
            </FormControl>

            {metodoPago === "MIXTO" && (
              <Box sx={{ mb: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={1}>
                  <TextField
                    label="Monto efectivo"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={montoEfectivo}
                    onChange={(e) => setMontoEfectivo(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Monto QR"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={montoQR}
                    onChange={(e) => setMontoQR(e.target.value)}
                    fullWidth
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Suma pago mixto
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={
                      Math.abs(
                        (Math.max(0, parseFloat(montoEfectivo) || 0) +
                          Math.max(0, parseFloat(montoQR) || 0)) -
                          totalFinalRedondeado
                      ) <= 0.01
                        ? "success.main"
                        : "warning.main"
                    }
                  >
                    {store.config.moneda}{" "}
                    {(
                      Math.max(0, parseFloat(montoEfectivo) || 0) +
                      Math.max(0, parseFloat(montoQR) || 0)
                    ).toFixed(2)}
                    {" / "}
                    {store.config.moneda} {totalFinalRedondeado.toFixed(2)}
                  </Typography>
                </Stack>
              </Box>
            )}

            <Autocomplete
              freeSolo
              size="small"
              options={campanasConCodigo}
              value={null}
              inputValue={codigoCampanaVenta}
              onInputChange={(_, value) => setCodigoCampanaVenta(value)}
              onChange={(_, value) => {
                if (!value) {
                  setCodigoCampanaVenta("");
                  return;
                }
                if (typeof value === "string") {
                  setCodigoCampanaVenta(value);
                  return;
                }
                setCodigoCampanaVenta(value.codigo || "");
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return option.codigo || "";
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {option.codigo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.canal} · {option.objetivo}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cupón / código campaña (opcional)"
                  placeholder="Ej: MAYOJEANS"
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<ShoppingCartCheckoutIcon />}
              onClick={abrirConfirmacion}
              disabled={carrito.length === 0}
              sx={{ py: 1.5, fontSize: "1rem", fontWeight: 700 }}
            >
              Cobrar
            </Button>

            {carrito.length > 0 && (
              <Button
                fullWidth
                size="small"
                color="inherit"
                onClick={limpiarCarrito}
                sx={{ mt: 1 }}
              >
                Limpiar carrito
              </Button>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => !procesandoVenta && setConfirmOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Confirmar venta
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {/* Cliente */}
          <Paper
            variant="outlined"
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.50",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <PersonOutlineIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700}>
                Cliente (opcional)
              </Typography>
            </Stack>

            <Autocomplete
              options={clientes}
              value={clienteSeleccionado}
              onChange={(_, value) => setClienteSeleccionado(value)}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              getOptionLabel={(c) =>
                `${c.nombre} (${formatPhoneDisplay(c.whatsapp) || c.whatsapp || "sin WA"})`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar por nombre o WhatsApp"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                  }}
                />
              )}
              sx={{ width: "100%" }}
            />

            {clienteSeleccionado ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "primary.light",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {clienteSeleccionado.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {formatPhoneDisplay(clienteSeleccionado.whatsapp) ||
                        clienteSeleccionado.whatsapp ||
                        "Sin WhatsApp"}
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={0.75}
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={
                          clienteSeleccionado?.stats?.segmento
                            ? `Segmento ${clienteSeleccionado.stats.segmento}`
                            : "Segmento NUEVO"
                        }
                      />
                      {interesesClienteVinculados.length > 0 && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`${interesesClienteVinculados.length} interés catálogo`}
                        />
                      )}
                    </Stack>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Quitar cliente"
                    onClick={() => setClienteSeleccionado(null)}
                    sx={{ mt: -0.5, mr: -0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    o registro rápido al confirmar
                  </Typography>
                </Divider>
                <Stack spacing={1.5}>
                  <TextField
                    label="Nombre"
                    size="small"
                    fullWidth
                    value={nuevoClienteNombre}
                    onChange={(e) => setNuevoClienteNombre(e.target.value)}
                    placeholder="Opcional"
                    sx={{
                      "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                    }}
                  />
                  <PhoneWithCountryField
                    label="WhatsApp"
                    value={nuevoClienteWhatsapp}
                    onChange={setNuevoClienteWhatsapp}
                    helperText="Opcional · código de país (+591)"
                    fullWidth
                  />
                  <Typography variant="caption" color="text.secondary">
                    Si completas nombre y WhatsApp, el cliente se guardará al confirmar el cobro.
                  </Typography>
                </Stack>
              </Box>
            )}
          </Paper>

          {/* Items */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Productos ({carrito.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell>Producto</TableCell>
                  <TableCell align="center">Cant.</TableCell>
                  <TableCell align="right">P. unit.</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {carrito.map((item) => (
                  <TableRow key={item.producto_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.nombre}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.marca || "—"} · {item.codigo_barras}
                        {item.talla ? ` · Talla ${item.talla}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{item.cantidad}</TableCell>
                    <TableCell align="right">
                      {moneda} {item.precio_unitario.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {moneda} {(item.cantidad * item.precio_unitario).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Descuentos */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Montos y descuentos
          </Typography>
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography fontWeight={600}>
                {moneda} {total.toFixed(2)}
              </Typography>
            </Stack>
            {descuentosFinales.length > 0 ? (
              descuentosFinales.map((promo) => (
                <Stack
                  key={promo.promo_id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {promo.nombre}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight={700}>
                    -{moneda} {Number(promo.monto).toFixed(2)}
                  </Typography>
                </Stack>
              ))
            ) : (
              <Typography variant="body2" color="text.disabled">
                Sin descuentos aplicados
              </Typography>
            )}
            {totalDescuentos > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" fontWeight={600}>
                  Total descuentos
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight={700}>
                  -{moneda} {totalDescuentos.toFixed(2)}
                </Typography>
              </Stack>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight={800}>
                Total a cobrar
              </Typography>
              <Typography variant="h6" fontWeight={800} color="primary">
                {moneda} {totalFinalRedondeado.toFixed(2)}
              </Typography>
            </Stack>
          </Stack>

          {/* Pago */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Fecha de la venta
          </Typography>
          <TextField
            type="date"
            value={fechaRegistro}
            onChange={(e) => setFechaRegistro(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2, maxWidth: 220 }}
            InputLabelProps={{ shrink: true }}
            label="Fecha"
            helperText="Usa otra fecha si estás registrando una venta olvidada"
          />

          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Pago
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              Método: <b>{metodoPagoLabel}</b>
            </Typography>
            {metodoPago === "MIXTO" && (
              <Typography variant="body2" color="text.secondary">
                Efectivo: {moneda} {(parseFloat(montoEfectivo) || 0).toFixed(2)} · QR:{" "}
                {moneda} {(parseFloat(montoQR) || 0).toFixed(2)}
              </Typography>
            )}
            {codigoCampanaVenta.trim() && (
              <Typography variant="body2" color="text.secondary">
                Cupón / campaña: <b>{codigoCampanaVenta.trim()}</b>
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={procesandoVenta}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarCobro}
            disabled={procesandoVenta || carrito.length === 0}
            startIcon={
              procesandoVenta ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ShoppingCartCheckoutIcon />
              )
            }
            sx={{ fontWeight: 700, minWidth: 160 }}
          >
            {procesandoVenta ? "Registrando…" : "Confirmar cobro"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
